import * as angular from "angular";
import * as _ from "lodash";
import { XeroService } from "../../../services/xero.service";
import { Campaign } from "../../../models/campaign.model";
import { Feature } from "../../../models/feature.model";
import { Organisation } from "../../../models/organisation.model";
import { User } from "../../../models/user.model";
import { AdminCampaignService } from "../../../services/admin-campaign.service";
import { CampaignService } from "../../../services/campaign.service";
import { CampaignDataProvider } from "../../../data-provider/campaign.dp";
import { UserService } from "../../../services/users.service";
import { DialogUtils } from "../../../helper/dialog.helper";
import { PermissionCheck } from "../../../shared/permission";
import { ToastUtils } from "../../../helper/toast.helper";
import {
    languagesConstants,
    LanguageCode,
} from "../../../utils/constants/languages.constants";

let EditCampaignComponent = {
    selector: "vcAdminEditCampaign",
    templateUrl:
        "/angular/components/admin/dialogs/edit-campaign-dialog.component.html",
    bindings: {
        campaign: "=",
        initialSection: "<?",
    },
    controller: class EditCampaignController {
        // * bindings for typescript compiler *
        public campaign: Campaign;
        public initialSection: string | null = null;
        // ************************************
        /** AV-17974: set once a link/unlink happened, so cancel still updates the row badge. */
        public xeroDirty = false;
        public loading = false;
        public langCodes: LanguageCode[] = languagesConstants.languages;
        public selectedLangCode: LanguageCode | null = null;
        public langCodeSearch: string = "";
        public organisations: Organisation[] = [];
        public features: Feature[] = [];
        public campaignKinds: any[] = [];
        public categoryId: number | null = null;
        public errors: any = [];
        public users: User[];
        public label = "Select Organization";
        public search = "";
        public wrongRegion: boolean;
        public section = "main";

        /**
         * Built once in $onInit: the shell's `sections` binding is a one-way
         * identity watch, so handing it a freshly built array every digest
         * would loop ($rootScope:infdig). Payment only applies to billable
         * accounts; organisation-type accounts (oid set) hide it.
         */
        public sections: { id: string; label: string; icon: string }[] = [];

        statusPillClass(): string {
            const name = this.campaign && this.campaign.campaignstatusname;
            if (this.campaign && this.campaign.campaigntype == "ORGANISATION")
                return "ec-pill-info";
            if (name == "Paid") return "ec-pill-success";
            if (name == "Trial") return "ec-pill-warning";
            if (name == "Sandbox") return "ec-pill-sandbox";
            if (name == "Legacy" || name == "Cancelled") return "ec-pill-error";
            return "ec-pill-neutral";
        }

        statusLabel(): string {
            const name = this.campaign && this.campaign.campaignstatusname;
            if (this.campaign && this.campaign.campaigntype == "ORGANISATION")
                return "Organisation";
            return name == "Legacy" ? "Paused" : name;
        }
        public editableFeatures = [
            "EXPORT",
            "CANVASSER_TRACKING_ALLOWED",
            "GEO_CODING",
            "USERLIMIT",
            "PUBLIC_API",
            "TURF_CUTTING",
            "2FA",
            "GO_APP",
            "REVIEW",
        ];
        public readonly statuses = [
            { campaignstatusid: "1", campaignstatusname: "Paid" },
            { campaignstatusid: "2", campaignstatusname: "Trial" },
            { campaignstatusid: "3", campaignstatusname: "Cancelled" },
            { campaignstatusid: "4", campaignstatusname: "Sandbox" },
            { campaignstatusid: "5", campaignstatusname: "Essentials" },
        ];

        private $scope: any;
        public __env: any;
        private dialog: DialogUtils;
        private toast: ToastUtils;
        private service: AdminCampaignService;
        private campaignService: CampaignService;
        private campaignDataProvider: CampaignDataProvider;
        private userService: UserService;
        private permissionProvider: PermissionCheck;

        constructor(
            __env: any,
            $scope: any,
            AdminCampaignService: AdminCampaignService,
            CampaignService: CampaignService,
            CampaignDataProvider: CampaignDataProvider,
            UserService: UserService,
            PermissionCheck: PermissionCheck,
            DialogUtils: DialogUtils,
            ToastUtils: ToastUtils,
            __app_type: string
        ) {
            this.$scope = $scope;
            this.__env = __env;
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.service = AdminCampaignService;
            this.campaignService = CampaignService;
            this.permissionProvider = PermissionCheck;
            this.userService = UserService;
            this.campaignDataProvider = CampaignDataProvider;
        }

        filterLangCodes(query: string): LanguageCode[] {
            const lowerQuery: string = query ? query.toLowerCase() : "";
            return lowerQuery
                ? this.langCodes.filter(
                      (language: LanguageCode): boolean =>
                          language.code.toLowerCase().includes(lowerQuery) ||
                          language.name.toLowerCase().includes(lowerQuery)
                  )
                : this.langCodes;
        }

        $onInit() {
            this.campaign = _.cloneDeep(this.campaign);

            // Payment holds Stripe (billable accounts only) and Xero (all).
            this.sections = [
                { id: "main", label: "Main details", icon: "badge" },
                { id: "payment", label: "Payment", icon: "credit_card" },
                { id: "limits", label: "Limits & features", icon: "tune" },
            ];
            if (
                this.initialSection &&
                this.sections.some((s) => s.id == this.initialSection)
            ) {
                this.section = this.initialSection;
            }

            if (this.campaign.langcode) {
                this.selectedLangCode =
                    this.langCodes.find(
                        (language: LanguageCode): boolean =>
                            language.code === this.campaign.langcode
                    ) ?? null;
            }
            this.wrongRegion =
                this.__env.region != this.campaign.campaignregion;
            this.campaignService
                .limits({ id: this.campaign.campaignid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.campaign.limits = json.data;
                    }
                });

            this.campaignDataProvider.getKinds().then((kinds: any[]) => {
                this.campaignKinds = kinds;
                if (this.campaign.campaignkind != null) {
                    for (var i = 0; i < kinds.length; i++) {
                        // AV-18114: stringify both sides. The Restler list
                        // returns campaignkind as a string and the Laravel
                        // save returns it as an int, and the saved value is
                        // merged straight back onto the row, so after one
                        // save a strict compare against the raw field stops
                        // matching and the Category select renders blank.
                        const selected = kinds[i].subcategories.find(
                            (subcategory: any) =>
                                `${subcategory.id}` ===
                                `${this.campaign.campaignkind}`
                        );
                        if (selected) {
                            this.categoryId = i;
                            return;
                        }
                    }
                }
            });

            this.userService
                .allCampaign({ id: this.campaign.campaignid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.users = json.data;
                    }
                });
            this.permissionProvider
                .getAdminFeatures(this.campaign.campaignid)
                .then((features: Feature[]) => {
                    angular.forEach(features, (feature: Feature) => {
                        if (
                            this.editableFeatures.includes(
                                feature.attributes.index
                            )
                        ) {
                            this.features.push(feature.attributes);
                        }
                    });
                });
            // AV-18114: only a real change of category resets the subtype.
            // categoryId starts null and is filled in by the getKinds()
            // callback, so without the null guard this fired on that first
            // fill and silently rewrote the account's saved subtype to the
            // first one in the category.
            this.$scope.$watch(
                () => this.categoryId,
                (newValue: any, oldValue: any) => {
                    if (
                        newValue === oldValue ||
                        oldValue === null ||
                        oldValue === undefined ||
                        !this.campaignKinds[newValue]
                    ) {
                        return;
                    }
                    const subcategory =
                        this.campaignKinds[newValue].subcategories[0];
                    if (subcategory) {
                        this.campaign.campaignkind = subcategory.id;
                    }
                },
                true
            );
        }

        saveCampaign(): void {
            if (this.loading == false) {
                this.loading = true;
                if (this.campaign.subscriptionid === "") {
                    this.campaign.subscriptionid = "none";
                }
                let data = _.cloneDeep(this.campaign);
                data.langcode = this.selectedLangCode
                    ? this.selectedLangCode.code
                    : null;
                data.features = this.features;
                this.service.update(data).then(
                    (response: object) => {
                        const json = response["data"];
                        if (json.success) {
                            this.errors = [];
                            this.toast.success("Account updated");
                            this.dialog.hide(_.merge(this.campaign, json.data));
                        } else {
                            this.errors = json.data;
                            this.toast.error();
                        }
                        this.loading = false;
                    },
                    (error: object) => {
                        this.toast.error(error);
                        this.loading = false;
                    }
                );
            }
        }

        /** Stripe and Xero are exclusive: a subscription id means Stripe. */
        hasStripe(): boolean {
            const id = this.campaign && this.campaign.subscriptionid;
            return !!id && id != "none";
        }

        /** AV-17974: keep the list row's badge truthful after a link/unlink. */
        xeroChanged(row: any): void {
            this.xeroDirty = true;
            angular.extend(this.campaign, XeroService.listFields(row));
        }

        cancel(): void {
            if (this.xeroDirty) {
                this.dialog.hide(
                    _.pick(
                        this.campaign,
                        Object.keys(XeroService.listFields(null))
                    )
                );
            } else {
                this.dialog.cancel();
            }
        }
    },
};
angular
    .module("admin")
    .component(EditCampaignComponent.selector, EditCampaignComponent);
